/**
 * Auth Redux Slice
 * Manages authentication state for both B2B and B2C users
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/auth/authService';
import tokenManager from '../../services/auth/tokenManager';
import { performLogout } from '../../utils/logout';

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  authType: null, // 'b2b' or 'b2c'
  organization: null, // Stytch organization
  loading: false, // Additional loading state for Stytch
  sessionExpiry: null, // Token expiry timestamp
  sessionWarningShown: false, // Track if warning was shown
  isRefreshingToken: false, // Track token refresh state
};

// Async thunks

// B2C Consumer Login
export const loginConsumer = createAsyncThunk(
  'auth/loginConsumer',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await authService.loginConsumer(email, password);
      
      if (response.success && response.data) {
        // Store token
        tokenManager.setToken(
          response.data.token,
          response.data.user,
          response.data.refresh_token
        );
        
        return {
          user: response.data.user,
          token: response.data.token,
          authType: 'b2c',
        };
      }
      
      return rejectWithValue(response.message || 'Login failed');
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

// B2C Consumer Registration
export const registerConsumer = createAsyncThunk(
  'auth/registerConsumer',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.registerConsumer(userData);
      
      if (response.success && response.data) {
        // Auto-login after registration
        tokenManager.setToken(
          response.data.token,
          response.data.user,
          response.data.refresh_token
        );
        
        return {
          user: response.data.user,
          token: response.data.token,
          authType: 'b2c',
        };
      }
      
      return rejectWithValue(response.message || 'Registration failed');
    } catch (error) {
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

// Handle B2B OAuth Callback
export const handleB2BCallback = createAsyncThunk(
  'auth/handleB2BCallback',
  async (code, { rejectWithValue }) => {
    try {
      const response = await authService.handleB2BCallback(code);
      
      if (response.success && response.data) {
        // Store token
        tokenManager.setToken(
          response.data.token,
          response.data.user,
          response.data.refresh_token
        );
        
        return {
          user: response.data.user,
          token: response.data.token,
          authType: 'b2b',
        };
      }
      
      return rejectWithValue(response.message || 'Authentication failed');
    } catch (error) {
      return rejectWithValue(error.message || 'Authentication failed');
    }
  }
);

// Get current user by verifying session
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      // Try session token first (Stytch B2B)
      const sessionToken = tokenManager.getSessionToken();
      if (sessionToken) {
        const response = await authService.getCurrentUser(sessionToken);
        
        if (response.success && response.data) {
          return {
            user: response.data.user,
            token: sessionToken,
            authType: 'stytch',
            sessionToken: sessionToken,
          };
        }
      }
      
      // Fallback to JWT token (platform or legacy)
      const jwtToken = tokenManager.getAccessToken();
      if (!jwtToken) {
        return rejectWithValue('No authentication token found');
      }
      
      // For JWT tokens, we assume user is already verified
      // This handles platform admin authentication
      const userData = tokenManager.getUserData();
      if (userData) {
        return {
          user: userData,
          token: jwtToken,
          authType: userData.isB2B ? 'b2b' : (userData.role === 'superAdmin' ? 'platform' : 'b2c'),
        };
      }
      
      return rejectWithValue('No valid authentication found');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get user');
    }
  }
);

// Logout
export const logout = createAsyncThunk(
  'auth/logout',
  async (options = {}) => {
    const { redirect = true, redirectTo } = options;
    await performLogout({ redirect, redirectTo });
    return true;
  }
);

// Reset password
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(email);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to reset password');
    }
  }
);

// Refresh token
export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const currentRefreshToken = tokenManager.getRefreshToken();

      const response = await authService.refreshToken(currentRefreshToken);

      // Cookie-only refresh flow
      if (response?.refreshed) {
        return {
          token: tokenManager.getAccessToken(),
          user: tokenManager.getUserData(),
          sessionExpiry: null,
        };
      }

      // Legacy token-based refresh flow
      if (response.success && response.data) {
        // Update tokens
        tokenManager.setToken(
          response.data.access_token,
          response.data.user,
          response.data.refresh_token
        );
        
        // Calculate new expiry
        const payload = tokenManager.parseJwt(response.data.access_token);
        const sessionExpiry = payload?.exp ? payload.exp * 1000 : null;
        
        return {
          token: response.data.access_token,
          user: response.data.user,
          sessionExpiry
        };
      }
      
      return rejectWithValue('Token refresh failed');
    } catch (error) {
      return rejectWithValue(error.message || 'Token refresh failed');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set user from stored token
    setUserFromToken: (state) => {
      const userData = tokenManager.getUserData();

      if (userData) {
        state.user = userData;
        state.isAuthenticated = true;
        state.authType = userData.isB2B ? 'b2b' : userData.isB2C ? 'b2c' : state.authType;
      } else {
        state.user = null;
        state.isAuthenticated = false;
        state.authType = null;
      }

      state.token = tokenManager.getAccessToken();
    },
    // Clear auth state
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.authType = null;
      state.error = null;
      state.organization = null;
      tokenManager.clearToken();
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Stytch-specific actions
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      if (!import.meta.env.PROD) {
        const payloadUser = action.payload?.user;
        console.log('[authSlice] loginSuccess', {
          role: payloadUser?.role,
          originalRole: payloadUser?.originalRole,
        });
        console.trace('[authSlice] loginSuccess trace');
      }
      state.loading = false;
      state.isLoading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.organization = action.payload.organization;
      state.isAuthenticated = true;
      state.authType = action.payload.authType || 'stytch';
      state.error = null;
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setOrganization: (state, action) => {
      state.organization = action.payload;
    },
    // Session management actions
    setSessionExpiry: (state, action) => {
      state.sessionExpiry = action.payload;
    },
    setSessionWarningShown: (state, action) => {
      state.sessionWarningShown = action.payload;
    },
    updateSessionFromToken: (state) => {
      // Calculate session expiry from current token
      const token = tokenManager.getAccessToken();
      if (token) {
        const payload = tokenManager.parseJwt(token);
        state.sessionExpiry = payload?.exp ? payload.exp * 1000 : null;
      } else {
        state.sessionExpiry = null;
      }
    },
  },
  extraReducers: (builder) => {
    // Login Consumer
    builder
      .addCase(loginConsumer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginConsumer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.authType = action.payload.authType;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginConsumer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Register Consumer
    builder
      .addCase(registerConsumer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerConsumer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.authType = action.payload.authType;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(registerConsumer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Handle B2B Callback
    builder
      .addCase(handleB2BCallback.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(handleB2BCallback.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.authType = action.payload.authType;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(handleB2BCallback.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Get Current User
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.authType = action.payload.authType;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.authType = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        // Even if logout fails, clear local state
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.authType = null;
        state.isAuthenticated = false;
        state.error = null;
      });

    // Reset Password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Refresh Token
    builder
      .addCase(refreshToken.pending, (state) => {
        state.isRefreshingToken = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isRefreshingToken = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.sessionExpiry = action.payload.sessionExpiry;
        state.sessionWarningShown = false;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.isRefreshingToken = false;
        state.error = action.payload;
        // If refresh fails, user needs to login again
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.sessionExpiry = null;
      });
  },
});

// Export actions
export const {
  setUserFromToken,
  clearAuth,
  clearError,
  loginStart,
  loginSuccess,
  loginFailure,
  setUser,
  setOrganization,
  setSessionExpiry,
  setSessionWarningShown,
  updateSessionFromToken
} = authSlice.actions;

// Export the logout thunk as logoutAction for backward compatibility
export { logout as logoutAction };

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthType = (state) => state.auth.authType;
export const selectIsLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;
export const selectSessionExpiry = (state) => state.auth.sessionExpiry;
export const selectSessionWarningShown = (state) => state.auth.sessionWarningShown;
export const selectIsRefreshingToken = (state) => state.auth.isRefreshingToken;

// Export reducer
export default authSlice.reducer;
